import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Picker } from '@react-native-picker/picker';
import { AlertCircleIcon, ChevronDown } from 'lucide-react-native';
import { useState } from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';
import { Modal } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { SurveyOtherTextInput } from './SurveyOtherTextInput';
import { SurveyQuestionLabel } from './SurveyQuestionLabel';
import { Button, ButtonText } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from '@/components/ui/select';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import {
  getSurveyOtherText,
  hasOtherItem,
  isOtherSelected,
  SURVEY_OTHER_VALUE,
  type SurveyElement,
} from '../../model/surveySchema';

const DONE_BUTTON_HIT_SLOP = getAccessibleHitSlop(36);

interface SurveyDropdownQuestionProps {
  element: SurveyElement;
  control: Control<FieldValues>;
  errorMessage?: string;
  /** Error for the Other free-text field, which is a separate form field from the choice. */
  otherErrorMessage?: string;
  hideRequiredMarker?: boolean;
}

/**
 * Renders a `dropdown` element. On web, this is a plain Gluestack `Select` —
 * its `SelectPortal` only mounts the Actionsheet on native, so on web
 * `Select` renders a native HTML `<select>` directly over the trigger and
 * never had the portal-stacking bug below, meaning the single-click browser
 * dropdown behavior it used to have is preserved.
 *
 * On native, this instead opens the `@react-native-picker/picker` wheel in a
 * bottom sheet. Gluestack's `Select` renders its Actionsheet via absolute
 * positioning in the React tree, which doesn't stack or receive touches
 * correctly inside this screen's full-screen modal presentation (confirmed
 * on-device: the trigger never opened the sheet). React Native's own `Modal`
 * creates a genuine separate native presentation via
 * `UIViewController.present` on iOS, so it layers correctly above an
 * already-presented full-screen modal regardless of where it's rendered in
 * the React tree — unlike the Actionsheet, it doesn't depend on a portal
 * target measured against the root navigator.
 */
export function SurveyDropdownQuestion({
  element,
  control,
  errorMessage,
  otherErrorMessage,
  hideRequiredMarker,
}: SurveyDropdownQuestionProps) {
  const choices = element.choices ?? [];

  const showsOther = hasOtherItem(element);

  const otherText = getSurveyOtherText(element);

  const [isOpen, setIsOpen] = useState(false);

  const { bottom } = useSafeAreaInsets();

  return (
    <Controller
      name={element.name}
      control={control}
      render={({ field: { onChange, value } }) => {
        const selectedValue = typeof value === 'string' ? value : '';

        // Every ordinary choice is its own label, but Other is not: its value is
        // the literal SurveyJS stores and its label is the author's wording.
        const selectedLabel = isOtherSelected(selectedValue) ? otherText : selectedValue;

        return (
          <FormControl
            isInvalid={!!errorMessage}
            aria-required={element.isRequired}
            className="gap-2">
            <SurveyQuestionLabel element={element} hideRequiredMarker={hideRequiredMarker} />

            {isWeb ? (
              /*
                Keyed on whether Other is the answer, which is the only case
                where the trigger's text differs from the stored value. Gluestack
                reads its label into state once at mount and refreshes it only
                when `selectedValue` is null, which this never is — so a draft
                restored after mount would otherwise leave the trigger showing
                the literal `other`. Flipping the key remounts it with the right
                label, and the flag changes rarely enough that nothing else
                notices.
              */
              <Select
                key={isOtherSelected(selectedValue) ? 'other' : 'choice'}
                selectedValue={selectedValue}
                selectedLabel={selectedLabel}
                initialLabel={selectedLabel}
                onValueChange={onChange}
                testID={element.name}>
                <SelectTrigger className="flex-row items-center justify-between rounded-lg border border-outline-300 px-4 py-3">
                  <SelectInput placeholder="Select an option" />
                  <SelectIcon as={ChevronDown} className="text-typography-500" />
                </SelectTrigger>

                <SelectPortal>
                  <SelectBackdrop />
                  <SelectContent>
                    <SelectDragIndicatorWrapper>
                      <SelectDragIndicator />
                    </SelectDragIndicatorWrapper>

                    <SelectScrollView>
                      {choices.map((choice) => (
                        <SelectItem key={choice} label={choice} value={choice} />
                      ))}

                      {showsOther ? (
                        <SelectItem
                          key={SURVEY_OTHER_VALUE}
                          label={otherText}
                          value={SURVEY_OTHER_VALUE}
                        />
                      ) : null}
                    </SelectScrollView>
                  </SelectContent>
                </SelectPortal>
              </Select>
            ) : (
              <>
                <Pressable
                  testID={element.name}
                  onPress={() => setIsOpen(true)}
                  {...getAccessibilityProps({
                    role: 'button',
                    label: `${element.title}${selectedLabel ? `, ${selectedLabel}` : ''}${element.isRequired ? ', required' : ''}`,
                  })}
                  className="flex-row items-center justify-between rounded-lg border border-outline-300 px-4 py-3">
                  <Text className={selectedValue ? 'text-typography-900' : 'text-typography-500'}>
                    {selectedLabel || 'Select an option'}
                  </Text>

                  <Icon as={ChevronDown} className="text-typography-500" />
                </Pressable>

                <Modal
                  visible={isOpen}
                  transparent
                  animationType="slide"
                  onRequestClose={() => setIsOpen(false)}>
                  <View className="flex-1 justify-end">
                    <Pressable
                      className="absolute inset-0 bg-black/40"
                      onPress={() => setIsOpen(false)}
                      {...getAccessibilityProps({ role: 'button', label: 'Close' })}
                    />

                    <View
                      className="rounded-t-2xl bg-background-0"
                      style={{ paddingBottom: bottom }}>
                      <HStack className="items-center justify-end border-b border-outline-200 px-4 py-2">
                        <Button
                          variant="link"
                          action="primary"
                          size="sm"
                          hitSlop={DONE_BUTTON_HIT_SLOP}
                          onPress={() => setIsOpen(false)}>
                          <ButtonText>Done</ButtonText>
                        </Button>
                      </HStack>

                      <Picker selectedValue={selectedValue} onValueChange={onChange}>
                        <Picker.Item label="Select an option" value="" />

                        {choices.map((choice) => (
                          <Picker.Item key={choice} label={choice} value={choice} />
                        ))}

                        {showsOther ? (
                          <Picker.Item
                            key={SURVEY_OTHER_VALUE}
                            label={otherText}
                            value={SURVEY_OTHER_VALUE}
                          />
                        ) : null}
                      </Picker>
                    </View>
                  </View>
                </Modal>
              </>
            )}

            {showsOther && isOtherSelected(selectedValue) ? (
              <SurveyOtherTextInput
                element={element}
                control={control}
                errorMessage={otherErrorMessage}
              />
            ) : null}

            <FormControlError>
              <FormControlErrorIcon as={AlertCircleIcon} />
              <FormControlErrorText>{errorMessage}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
